import apiClient from './client';
import type {
  WorkItem,
  DailyPlan,
  AnnualStrategicDriver,
  CalendarEvent,
  ExecutiveContext,
  PlanningPreference,
  ImageInput,
  ImageAnalysisResult,
  StartMyDayResponse,
  CloseDayResponse,
  CapacitySummary,
  Tag,
  Category,
  FocusWindow,
  CalendarSource,
  ExecutiveAssignment,
  PromptConfiguration,
  AuditLogEntry,
  MeetingTranscript,
  UsageMeter,
  BillingProduct,
  PrincipalProfileEntry,
  PrincipalReminder,
  RestaurantResult,
  WebhookSubscription,
  Project,
  ProjectMember,
  ProjectOutcome,
  ProjectIndicator,
  IndicatorSnapshot,
  MeetingPrepResponse,
} from '../types/models';

// Work Items
export const workItemsApi = {
  getAll: (forExecId?: string) => apiClient.get<WorkItem[]>('/workitems', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  getById: (id: string) => apiClient.get<WorkItem>(`/workitems/${id}`).then(r => r.data),
  getByDate: (date: string, forExecId?: string) => apiClient.get<WorkItem[]>(`/workitems/date/${date}`, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  getByStatus: (status: string) => apiClient.get<WorkItem[]>(`/workitems/status/${status}`).then(r => r.data),
  create: (data: Partial<WorkItem>) => apiClient.post<WorkItem>('/workitems', data).then(r => r.data),
  update: (id: string, data: Partial<WorkItem>) => apiClient.put<WorkItem>(`/workitems/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/workitems/${id}`),
  carryForward: (id: string, newDate: string) => apiClient.post<WorkItem>(`/workitems/${id}/carry-forward`, JSON.stringify(newDate)).then(r => r.data),
  delegate: (id: string, data: { targetExecutiveUserId: string; targetDate?: string; notes?: string }) =>
    apiClient.post(`/workitems/${id}/delegate`, data).then(r => r.data),
  getRecurring: (forExecId?: string) =>
    apiClient.get<WorkItem[]>('/workitems/recurring', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
};

// Daily Plans
export const dailyPlanApi = {
  getByDate: (date: string, forExecId?: string) => apiClient.get<DailyPlan>(`/dailyplan/${date}`, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  generate: (date: string, data?: { dailyTheme?: string; additionalInstructions?: string; trigger?: string }) =>
    apiClient.post<StartMyDayResponse>(`/dailyplan/${date}/generate`, data || {}).then(r => r.data),
  approve: (date: string) => apiClient.post<DailyPlan>(`/dailyplan/${date}/approve`).then(r => r.data),
  activate: (date: string) => apiClient.post<DailyPlan>(`/dailyplan/${date}/activate`).then(r => r.data),
  close: (date: string) => apiClient.post<DailyPlan>(`/dailyplan/${date}/close`).then(r => r.data),
  updateNotes: (date: string, notes: string) => apiClient.put<DailyPlan>(`/dailyplan/${date}/notes`, { notes }).then(r => r.data),
};

// Operating Rhythm
export const rhythmApi = {
  startMyDay: (data?: { date?: string; dailyTheme?: string }) =>
    apiClient.post<StartMyDayResponse>('/operatingrhythm/start-my-day', data || {}).then(r => r.data),
  replan: (data?: { date?: string; dailyTheme?: string }) =>
    apiClient.post<StartMyDayResponse>('/operatingrhythm/replan', data || {}).then(r => r.data),
  closeDay: (data: { date?: string; reflectionContent?: string; insights?: string; adjustmentsMade?: string }) =>
    apiClient.post<CloseDayResponse>('/operatingrhythm/close-day', data).then(r => r.data),
  planTomorrow: (data?: { dailyTheme?: string }) =>
    apiClient.post<StartMyDayResponse>('/operatingrhythm/plan-tomorrow', data || {}).then(r => r.data),
  getCapacity: (date: string) =>
    apiClient.get<CapacitySummary>(`/operatingrhythm/capacity/${date}`).then(r => r.data),
};

// Strategic Drivers
export const driversApi = {
  getAll: () => apiClient.get<AnnualStrategicDriver[]>('/strategicdrivers').then(r => r.data),
  getActive: () => apiClient.get<AnnualStrategicDriver[]>('/strategicdrivers/active').then(r => r.data),
  getCurrent: () => apiClient.get('/strategicdrivers/current').then(r => r.data),
  getById: (id: string) => apiClient.get<AnnualStrategicDriver>(`/strategicdrivers/${id}`).then(r => r.data),
  create: (data: Partial<AnnualStrategicDriver>) => apiClient.post<AnnualStrategicDriver>('/strategicdrivers', data).then(r => r.data),
  update: (id: string, data: Partial<AnnualStrategicDriver>) => apiClient.put<AnnualStrategicDriver>(`/strategicdrivers/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/strategicdrivers/${id}`),
};

// Calendar
export const calendarApi = {
  getByDate: (date: string, forExecId?: string) => apiClient.get<CalendarEvent[]>(`/calendar/${date}`, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  getByRange: (from: string, to: string, forExecId?: string) => apiClient.get<CalendarEvent[]>('/calendar/range', { params: { from, to, forExecutiveUserId: forExecId } }).then(r => r.data),
  sync: () => apiClient.post<CalendarEvent[]>('/calendar/sync').then(r => r.data),
  linkToDriver: (id: string, data: { strategicDriverId?: string | null; applyToRecurring?: boolean }) =>
    apiClient.put(`/calendar/${id}/strategic-driver`, data).then(r => r.data),
  linkToProject: (id: string, data: { projectId?: string | null }) =>
    apiClient.put(`/calendar/${id}/project`, data).then(r => r.data),
  getMeetingPrep: (eventId: string, forExecId?: string) =>
    apiClient.get<MeetingPrepResponse>(`/calendar/event/${eventId}/prep`, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
};

// Context
export const contextApi = {
  getAll: () => apiClient.get<ExecutiveContext[]>('/context').then(r => r.data),
  getByLayer: (layer: string) => apiClient.get<ExecutiveContext[]>(`/context/layer/${layer}`).then(r => r.data),
  create: (data: Partial<ExecutiveContext>) => apiClient.post<ExecutiveContext>('/context', data).then(r => r.data),
  update: (id: string, data: Partial<ExecutiveContext>) => apiClient.put<ExecutiveContext>(`/context/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/context/${id}`),
};

// Preferences
export const preferencesApi = {
  get: () => apiClient.get<PlanningPreference>('/preferences').then(r => r.data),
  update: (data: Partial<PlanningPreference>) => apiClient.put<PlanningPreference>('/preferences', data).then(r => r.data),
};

// Auth / MFA / User Management
export const authApi = {
  setupMfa: () => apiClient.post<{ sharedKey: string; authenticatorUri: string }>('/auth/mfa/setup').then(r => r.data),
  enableMfa: (code: string) => apiClient.post<{ message: string; recoveryCodes: string[] }>('/auth/mfa/enable', { code }).then(r => r.data),
  getUsers: () => apiClient.get<Array<{
    id: string; email: string; displayName: string; role: string;
    isActive: boolean; isTenantAdmin: boolean; createdAt: string; lastLoginAt: string | null; mfaEnabled: boolean;
  }>>('/auth/users').then(r => r.data),
  registerUser: (data: { email: string; password: string; displayName: string; tenantId: string; role?: string }) =>
    apiClient.post('/auth/register', data).then(r => r.data),
  toggleUserActive: (id: string) => apiClient.delete(`/auth/users/${id}`).then(r => r.data),
  me: () => apiClient.get<{
    id: string; email: string; displayName: string; tenantId: string; role: string; mfaEnabled: boolean;
  }>('/auth/me').then(r => r.data),
  resetPassword: (data: { email: string; token: string; newPassword: string }) =>
    apiClient.post('/auth/reset-password', data).then(r => r.data),
  revokeSessions: () =>
    apiClient.post<{ message: string; accessToken: string; refreshToken: string }>('/auth/revoke-sessions').then(r => r.data),
  revokeUserSessions: (userId: string) =>
    apiClient.post<{ message: string }>(`/auth/users/${userId}/revoke-sessions`).then(r => r.data),
};

// API Keys
export const apiKeysApi = {
  list: () => apiClient.get('/auth/api-keys').then(r => r.data) as Promise<Array<{
    id: string; name: string; keyPrefix: string; createdAt: string;
    expiresAt: string | null; lastUsedAt: string | null; scopes: string | null;
  }>>,
  create: (data: { name: string; expiryDays?: number; scopes?: string }) =>
    apiClient.post('/auth/api-keys', data).then(r => r.data) as Promise<{
      id: string; name: string; keyPrefix: string; key: string;
      expiresAt: string | null; message: string;
    }>,
  revoke: (id: string) => apiClient.delete(`/auth/api-keys/${id}`).then(r => r.data),
};

// Images
export const imagesApi = {
  getAll: () => apiClient.get<ImageInput[]>('/images').then(r => r.data),
  getById: (id: string) => apiClient.get<ImageInput>(`/images/${id}`).then(r => r.data),
  upload: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImageInput>('/images/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  analyse: (id: string) => apiClient.post<ImageAnalysisResult[]>(`/images/${id}/analyse`).then(r => r.data),
  accept: (id: string, items: ImageAnalysisResult[]) => apiClient.post<WorkItem[]>(`/images/${id}/accept`, items).then(r => r.data),
  discard: (id: string) => apiClient.post(`/images/${id}/discard`),
};

// Tags
export const tagsApi = {
  getAll: () => apiClient.get<Tag[]>('/tags').then(r => r.data),
  getById: (id: string) => apiClient.get<Tag>(`/tags/${id}`).then(r => r.data),
  create: (data: Partial<Tag>) => apiClient.post<Tag>('/tags', data).then(r => r.data),
  update: (id: string, data: Partial<Tag>) => apiClient.put<Tag>(`/tags/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/tags/${id}`),
};

// Categories
export const categoriesApi = {
  getAll: () => apiClient.get<Category[]>('/categories').then(r => r.data),
  getById: (id: string) => apiClient.get<Category>(`/categories/${id}`).then(r => r.data),
  create: (data: Partial<Category>) => apiClient.post<Category>('/categories', data).then(r => r.data),
  update: (id: string, data: Partial<Category>) => apiClient.put<Category>(`/categories/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/categories/${id}`),
};

// Focus Windows
export const focusWindowsApi = {
  getAll: () => apiClient.get<FocusWindow[]>('/focuswindows').then(r => r.data),
  getById: (id: string) => apiClient.get<FocusWindow>(`/focuswindows/${id}`).then(r => r.data),
  getByDay: (day: string) => apiClient.get<FocusWindow[]>(`/focuswindows/day/${day}`).then(r => r.data),
  create: (data: Partial<FocusWindow>) => apiClient.post<FocusWindow>('/focuswindows', data).then(r => r.data),
  update: (id: string, data: Partial<FocusWindow>) => apiClient.put<FocusWindow>(`/focuswindows/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/focuswindows/${id}`),
};

// Calendar Connect (OAuth)
export const calendarConnectApi = {
  connectMicrosoft: (data: { name?: string; isPersonal?: boolean; eaVisibility?: string; returnPath?: string }) =>
    apiClient.post('/calendarconnect/microsoft', data).then(r => r.data),
  connectGoogle: (data: { name?: string; isPersonal?: boolean; eaVisibility?: string; returnPath?: string }) =>
    apiClient.post('/calendarconnect/google', data).then(r => r.data),
  sync: (sourceId: string) =>
    apiClient.post(`/calendarconnect/${sourceId}/sync`).then(r => r.data),
  disconnect: (sourceId: string) =>
    apiClient.delete(`/calendarconnect/${sourceId}`).then(r => r.data),
};

// Calendar Sources
export const calendarSourcesApi = {
  getAll: () => apiClient.get<CalendarSource[]>('/calendarsources').then(r => r.data),
  getById: (id: string) => apiClient.get<CalendarSource>(`/calendarsources/${id}`).then(r => r.data),
  create: (data: Partial<CalendarSource>) => apiClient.post<CalendarSource>('/calendarsources', data).then(r => r.data),
  update: (id: string, data: Partial<CalendarSource>) => apiClient.put<CalendarSource>(`/calendarsources/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/calendarsources/${id}`),
  sync: (id: string) => apiClient.post<CalendarSource>(`/calendarsources/${id}/sync`).then(r => r.data),
  createEvent: (sourceId: string, data: any) =>
    apiClient.post(`/calendarsources/${sourceId}/events`, data).then(r => r.data),
};

// Executive Assignments
export const executiveAssignmentsApi = {
  getAll: () => apiClient.get<ExecutiveAssignment[]>('/executiveassignments').then(r => r.data),
  create: (data: Partial<ExecutiveAssignment>) => apiClient.post<ExecutiveAssignment>('/executiveassignments', data).then(r => r.data),
  deactivate: (id: string) => apiClient.post<ExecutiveAssignment>(`/executiveassignments/${id}/deactivate`).then(r => r.data),
};

// Prompt Configurations
export const promptConfigurationsApi = {
  getAll: () => apiClient.get<PromptConfiguration[]>('/promptconfigurations').then(r => r.data),
  getByKey: (key: string) => apiClient.get<PromptConfiguration>(`/promptconfigurations/key/${key}`).then(r => r.data),
  getHistory: (key: string) => apiClient.get<PromptConfiguration[]>(`/promptconfigurations/key/${key}/history`).then(r => r.data),
  create: (data: Partial<PromptConfiguration>) => apiClient.post<PromptConfiguration>('/promptconfigurations', data).then(r => r.data),
  activate: (id: string) => apiClient.post<PromptConfiguration>(`/promptconfigurations/${id}/activate`).then(r => r.data),
};

// Audit Logs
export const auditLogsApi = {
  getAll: (params?: { action?: string; entityType?: string; from?: string; to?: string }) =>
    apiClient.get<AuditLogEntry[]>('/auditlogs', { params }).then(r => r.data),
};

// Transcripts
export const transcriptsApi = {
  getAll: (forExecId?: string) =>
    apiClient.get<MeetingTranscript[]>('/transcripts', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  getById: (id: string) =>
    apiClient.get<MeetingTranscript>(`/transcripts/${id}`).then(r => r.data),
  upload: (data: { title: string; meetingDate: string; attendees?: string; transcriptText: string }, forExecId?: string) =>
    apiClient.post<MeetingTranscript>('/transcripts', data, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  analyse: (id: string, forExecId?: string) =>
    apiClient.post<MeetingTranscript>(`/transcripts/${id}/analyse`, {}, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  accept: (id: string, extractionIds: string[], forExecId?: string) =>
    apiClient.post(`/transcripts/${id}/accept`, { extractionIds }, { params: { forExecutiveUserId: forExecId } }),
};

// Usage
export const usageApi = {
  get: (year: number, month: number) =>
    apiClient.get<UsageMeter[]>(`/usage/${year}/${month}`).then(r => r.data),
};

// Billing
export const billingApi = {
  getPlans: () => apiClient.get<BillingProduct[]>('/billing/plans').then(r => r.data),
  checkout: (data: { productId: string; email: string; organisationName: string; executiveName: string; seatCount: number }) =>
    apiClient.post<{ checkoutUrl: string }>('/billing/checkout', data).then(r => r.data),
  getStatus: () => apiClient.get('/billing/status').then(r => r.data),
  getPortalUrl: () => apiClient.post<{ portalUrl: string }>('/billing/portal').then(r => r.data),
};

// Tenant Administration
export const tenantAdminApi = {
  getTenant: () => apiClient.get('/tenantadmin/tenant').then(r => r.data),
  getUsers: () => apiClient.get('/tenantadmin/users').then(r => r.data),
  getUsage: (year?: number, month?: number) =>
    apiClient.get('/tenantadmin/usage', { params: { year, month } }).then(r => r.data),
  updateBillingEmail: (billingEmail: string) =>
    apiClient.put('/tenantadmin/billing-email', { billingEmail }).then(r => r.data),
  updateUserRole: (userId: string, role: string) =>
    apiClient.put(`/tenantadmin/users/${userId}/role`, { role }).then(r => r.data),
  updateUserStatus: (userId: string, isActive: boolean) =>
    apiClient.put(`/tenantadmin/users/${userId}/status`, { isActive }).then(r => r.data),
  exportData: () =>
    apiClient.get('/tenantadmin/export', { responseType: 'blob' }).then(r => r),
  resendWelcomeEmail: (userId: string) => apiClient.post(`/tenantadmin/users/${userId}/resend-welcome`).then(r => r.data),
  updateSeats: (executiveSeats: number) =>
    apiClient.post('/tenantadmin/seats/update', { executiveSeats }).then(r => r.data),
  cancelTenant: (mfaCode: string) =>
    apiClient.post('/tenantadmin/cancel', {}, { headers: { 'X-MFA-Code': mfaCode } }).then(r => r.data),
  getBranding: () => apiClient.get('/tenantadmin/branding').then(r => r.data),
  updateBranding: (data: any) => apiClient.put('/tenantadmin/branding', data).then(r => r.data),
  getFinancialYear: () => apiClient.get('/tenantadmin/financial-year').then(r => r.data),
  updateFinancialYear: (data: { useFinancialYear: boolean; startMonth?: number }) =>
    apiClient.put('/tenantadmin/financial-year', data).then(r => r.data),
};

// Reflections
export const reflectionsApi = {
  getByDate: (date: string) => apiClient.get(`/reflections/date/${date}`).then(r => r.data),
  getById: (id: string) => apiClient.get(`/reflections/${id}`).then(r => r.data),
  create: (data: { date: string; trigger: string; content: string; insights?: string; adjustmentsMade?: string }) =>
    apiClient.post('/reflections', data).then(r => r.data),
};

// Reporting
export const reportingApi = {
  getExecutiveReport: (from: string, to: string, forExecId?: string) =>
    apiClient.get('/reporting/executive', { params: { from, to, forExecutiveUserId: forExecId } }).then(r => r.data),
  getCrossExecutiveReport: (from: string, to: string) =>
    apiClient.get('/reporting/cross-executive', { params: { from, to } }).then(r => r.data),
  getCapacityForecast: (weeksAhead?: number) =>
    apiClient.get('/reporting/forecast', { params: { weeksAhead } }).then(r => r.data),
};

// Platform Administration
export const platformApi = {
  getStats: () => apiClient.get('/platform/stats').then(r => r.data),
  getTenants: (status?: string, plan?: string) =>
    apiClient.get('/platform/tenants', { params: { status, plan } }).then(r => r.data),
  getTenant: (id: string) => apiClient.get(`/platform/tenants/${id}`).then(r => r.data),
  createTenant: (data: any) => apiClient.post('/platform/tenants', data).then(r => r.data),
  updateTenant: (id: string, data: any) => apiClient.put(`/platform/tenants/${id}`, data).then(r => r.data),
  suspendTenant: (id: string) => apiClient.post(`/platform/tenants/${id}/suspend`).then(r => r.data),
  reactivateTenant: (id: string) => apiClient.post(`/platform/tenants/${id}/reactivate`).then(r => r.data),
  deleteTenant: (id: string, mfaCode: string) =>
    apiClient.delete(`/platform/tenants/${id}`, { headers: { 'X-MFA-Code': mfaCode } }).then(r => r.data),
  hardDeleteTenant: (id: string, mfaCode: string) =>
    apiClient.post(`/platform/tenants/${id}/hard-delete`, {}, { headers: { 'X-MFA-Code': mfaCode } }).then(r => r.data),
  getProducts: () => apiClient.get('/platform/products').then(r => r.data),
  createProduct: (data: any) => apiClient.post('/platform/products', data).then(r => r.data),
  updateProduct: (id: string, data: any) => apiClient.put(`/platform/products/${id}`, data).then(r => r.data),
  syncProductToStripe: (id: string) => apiClient.post(`/platform/products/${id}/sync-stripe`).then(r => r.data),
  getEmailSettings: () => apiClient.get('/platform/email-settings').then(r => r.data),
  updateEmailSettings: (data: any) => apiClient.put('/platform/email-settings', data).then(r => r.data),
  sendTestEmail: (toEmail: string) => apiClient.post('/platform/email-settings/test', { toEmail }).then(r => r.data),
  getEmailLogs: (limit?: number) => apiClient.get('/platform/email-logs', { params: { limit } }).then(r => r.data),
  getUsers: (tenantId?: string) => apiClient.get('/platform/users', { params: { tenantId } }).then(r => r.data),
  updateUser: (userId: string, data: any) =>
    apiClient.put(`/platform/users/${userId}`, data).then(r => r.data),
  createPlatformAdmin: (data: any) => apiClient.post('/platform/admins', data).then(r => r.data),
  getStripeSettings: () => apiClient.get('/platform/stripe-settings').then(r => r.data),
  updateStripeSettings: (data: any) => apiClient.put('/platform/stripe-settings', data).then(r => r.data),
  getAiSettings: () => apiClient.get('/platform/ai-settings').then(r => r.data),
  updateAiSettings: (data: any) => apiClient.put('/platform/ai-settings', data).then(r => r.data),
  resendWelcomeEmail: (userId: string) => apiClient.post(`/platform/users/${userId}/resend-welcome`).then(r => r.data),
  getAuditLogs: (params?: { tenantId?: string; userId?: string; action?: string; from?: string; to?: string; limit?: number }) =>
    apiClient.get('/platform/audit-logs', { params }).then(r => r.data),
  resetUserPassword: (userId: string) => apiClient.post(`/platform/users/${userId}/reset-password`).then(r => r.data),
  getBillingOverview: () => apiClient.get('/platform/billing/overview').then(r => r.data),
  getBillingTenants: (params?: { status?: string; plan?: string }) =>
    apiClient.get('/platform/billing/tenants', { params }).then(r => r.data),
  getTenantBillingHistory: (tenantId: string) =>
    apiClient.get(`/platform/billing/tenants/${tenantId}/history`).then(r => r.data),
};

// Principal Profile
export const principalProfileApi = {
  getProfile: (execId: string) => apiClient.get<Record<string, PrincipalProfileEntry[]>>(`/principalprofile/${execId}`).then(r => r.data),
  getSection: (execId: string, section: string) => apiClient.get<PrincipalProfileEntry[]>(`/principalprofile/${execId}/section/${section}`).then(r => r.data),
  getReminders: (execId: string, daysAhead?: number) => apiClient.get<PrincipalReminder[]>(`/principalprofile/${execId}/reminders`, { params: { daysAhead } }).then(r => r.data),
  addEntry: (execId: string, data: Partial<PrincipalProfileEntry>) => apiClient.post<PrincipalProfileEntry>(`/principalprofile/${execId}`, data).then(r => r.data),
  updateEntry: (execId: string, entryId: string, data: Partial<PrincipalProfileEntry>) => apiClient.put<PrincipalProfileEntry>(`/principalprofile/${execId}/${entryId}`, data).then(r => r.data),
  deleteEntry: (execId: string, entryId: string) => apiClient.delete(`/principalprofile/${execId}/${entryId}`),
  seedTemplate: (execId: string) => apiClient.post<PrincipalProfileEntry[]>(`/principalprofile/${execId}/seed-template`).then(r => r.data),
};

// Restaurant Search
export const restaurantsApi = {
  search: (location: string, cuisine?: string, radius?: number) =>
    apiClient.get<RestaurantResult[]>('/restaurants/search', { params: { location, cuisine, radius } }).then(r => r.data),
};

// Webhooks
export const webhooksApi = {
  getAll: () => apiClient.get<WebhookSubscription[]>('/webhooks').then(r => r.data),
  create: (data: { url: string; event: string; secret?: string }) => apiClient.post<WebhookSubscription>('/webhooks', data).then(r => r.data),
  update: (id: string, data: { url?: string; isActive?: boolean }) => apiClient.put<WebhookSubscription>(`/webhooks/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/webhooks/${id}`),
};

// Notifications
export const notificationsApi = {
  getAll: (includeRead = false) =>
    apiClient.get('/notifications', { params: { includeRead } }).then(r => r.data) as Promise<Array<{
      id: string; title: string; message?: string; type: string;
      entityType?: string; entityId?: string; isRead: boolean;
      readAt?: string; createdAt: string;
    }>>,
  markAsRead: (id: string) =>
    apiClient.post(`/notifications/${id}/read`).then(r => r.data),
  markAllAsRead: () =>
    apiClient.post('/notifications/read-all').then(r => r.data),
  delete: (id: string) =>
    apiClient.delete(`/notifications/${id}`),
};

// CSV Exports
export const exportApi = {
  downloadWorkItemsCsv: (forExecId?: string) =>
    apiClient.get('/export/work-items/csv', { responseType: 'blob', params: { forExecutiveUserId: forExecId } }).then(r => r),
  downloadProjectsCsv: (forExecId?: string) =>
    apiClient.get('/export/projects/csv', { responseType: 'blob', params: { forExecutiveUserId: forExecId } }).then(r => r),
  downloadKpiHistoryCsv: (driverId?: string, forExecId?: string) =>
    apiClient.get('/export/kpi-history/csv', { responseType: 'blob', params: { driverId, forExecutiveUserId: forExecId } }).then(r => r),
};

// Feature Requests
export const featureRequestsApi = {
  getAll: () => apiClient.get('/featurerequests').then(r => r.data),
  create: (data: any) => apiClient.post('/featurerequests', data).then(r => r.data),
  vote: (id: string) => apiClient.post(`/featurerequests/${id}/vote`).then(r => r.data),
};

// Projects
export const projectsApi = {
  getAll: (params?: { status?: string; driverId?: string; includeArchived?: boolean }) =>
    apiClient.get<Project[]>('/projects', { params }).then(r => r.data),
  getById: (id: string) => apiClient.get<Project>(`/projects/${id}`).then(r => r.data),
  getPortfolio: () => apiClient.get<Project[]>('/projects/portfolio').then(r => r.data),
  create: (data: Partial<Project>) => apiClient.post<Project>('/projects', data).then(r => r.data),
  update: (id: string, data: Partial<Project>) => apiClient.put<Project>(`/projects/${id}`, data).then(r => r.data),
  archive: (id: string) => apiClient.post(`/projects/${id}/archive`).then(r => r.data),
  restore: (id: string) => apiClient.post(`/projects/${id}/restore`).then(r => r.data),
  // Members
  getMembers: (id: string) => apiClient.get<ProjectMember[]>(`/projects/${id}/members`).then(r => r.data),
  addMember: (id: string, data: Partial<ProjectMember>) => apiClient.post<ProjectMember>(`/projects/${id}/members`, data).then(r => r.data),
  updateMember: (id: string, memberId: string, data: Partial<ProjectMember>) => apiClient.put<ProjectMember>(`/projects/${id}/members/${memberId}`, data).then(r => r.data),
  removeMember: (id: string, memberId: string) => apiClient.delete(`/projects/${id}/members/${memberId}`).then(r => r.data),
  // Outcomes
  getOutcomes: (id: string) => apiClient.get<ProjectOutcome[]>(`/projects/${id}/outcomes`).then(r => r.data),
  createOutcome: (id: string, data: Partial<ProjectOutcome>) => apiClient.post<ProjectOutcome>(`/projects/${id}/outcomes`, data).then(r => r.data),
  updateOutcome: (id: string, outcomeId: string, data: Partial<ProjectOutcome>) => apiClient.put<ProjectOutcome>(`/projects/${id}/outcomes/${outcomeId}`, data).then(r => r.data),
  deleteOutcome: (id: string, outcomeId: string) => apiClient.delete(`/projects/${id}/outcomes/${outcomeId}`).then(r => r.data),
  // Indicators
  getIndicators: (id: string) => apiClient.get<ProjectIndicator[]>(`/projects/${id}/indicators`).then(r => r.data),
  createIndicator: (id: string, data: Partial<ProjectIndicator>) => apiClient.post<ProjectIndicator>(`/projects/${id}/indicators`, data).then(r => r.data),
  updateIndicator: (id: string, indicatorId: string, data: Partial<ProjectIndicator>) => apiClient.put<ProjectIndicator>(`/projects/${id}/indicators/${indicatorId}`, data).then(r => r.data),
  deleteIndicator: (id: string, indicatorId: string) => apiClient.delete(`/projects/${id}/indicators/${indicatorId}`).then(r => r.data),
  recordValue: (id: string, indicatorId: string, data: { value: number; notes?: string }) => apiClient.post<IndicatorSnapshot>(`/projects/${id}/indicators/${indicatorId}/record`, data).then(r => r.data),
  getHistory: (id: string, indicatorId: string) => apiClient.get<IndicatorSnapshot[]>(`/projects/${id}/indicators/${indicatorId}/history`).then(r => r.data),
  // Report
  getReport: (id: string) => apiClient.get(`/projects/${id}/report`).then(r => r.data),
};
